import React, { useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { Modal } from 'semantic-ui-react';

import {
  UPDATE_BRIEF_RESPONSE_ANSWERS,
  SG_UPDATE_BRIEF_RESPONSE,
  SG_START_BRIEF_SURVEY
} from 'constants/actions';

export function SurveyInProgressDialog({
  setQuestionNo,
  setVisualQuestionNo,
  briefResponseId,
  token,
  resetValidationToken,
  storedResponse,
  setCurrentQuestionNo,
  setCurrentQuestionDet,
  setQuestionSequenceMap,
  setShowingStoredResponse,
  setStoredResponseFound,
  setStoredResponse,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);

  function restartSurvey() {
    const NewValidationToken = uuidv4();

    localStorage.setItem('validation_token', NewValidationToken);
    resetValidationToken(NewValidationToken);

    // Clear local state
    setStoredResponseFound(false);
    setStoredResponse(false);
    setQuestionSequenceMap(false);
    setCurrentQuestionNo(1);
    setQuestionNo(1);
    setCurrentQuestionDet(undefined);
    setVisualQuestionNo(0);
    setOpen(false);

    // Send reset to backend
    dispatch({
      type: SG_UPDATE_BRIEF_RESPONSE,
      payload: {
        id: briefResponseId,
        query: `?token=${token}`,
        validation_token: NewValidationToken,
        reset_stored_data: true,
      },
    });

    // Reload the page to start fresh (ensures backend update completes before refetch)
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  function loadSavedSurvey() {
    let NUMS = storedResponse?.current_question;
    if (NUMS) {
      const _checkFdbk = storedResponse?.questionSequenceMap?.[NUMS]?.response?.skipped;
      if (_checkFdbk) NUMS--;
      setCurrentQuestionNo(NUMS);
      setCurrentQuestionDet(storedResponse?.questionSequenceMap?.[NUMS]);
      setQuestionSequenceMap(storedResponse?.questionSequenceMap);
    }

    dispatch({
      type: UPDATE_BRIEF_RESPONSE_ANSWERS,
      payload: storedResponse,
    });

    setShowingStoredResponse(true);
    setQuestionNo(NUMS);
    setOpen(false);
    setVisualQuestionNo(storedResponse?.visual_question || NUMS);
  }

  return (
    <Modal open={open} onClose={() => setOpen(false)} size="small" dimmer="inverted" closeOnEscape={false} closeOnDimmerClick={false}>
      <Card>
        <Title>We found your survey in-progress</Title>
        <Subtitle>
          You’ve already answered some questions. Would you like to load your previous
          responses or start over?
        </Subtitle>

        <Actions>
          <GhostButton
            id="btn-existingdata-reset"
            onClick={restartSurvey}
            aria-label="Restart survey"
          >
            Restart
          </GhostButton>

          <PrimaryButton
            id="btn-existingdata-resume"
            onClick={loadSavedSurvey}
            aria-label="Load saved survey"
          >
            Load Saved
          </PrimaryButton>
        </Actions>
      </Card>
    </Modal>
  );
}

/* -------- styled-components -------- */

const Card = styled.div`
  width: min(560px, calc(100vw - 40px));
  margin: 32px auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 28px 24px 22px;
  text-align: center;
  font-family: 'Raleway', sans-serif;
  animation: pop 160ms ease-out;
  @keyframes pop {
    from {
      transform: scale(0.985);
      opacity: 0.8;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const Title = styled.h3`
  margin: 6px 0 8px 0;
  color: #2a3039;
  font-weight: 700;
  font-size: 20px;
  line-height: 1.3;
`;

const Subtitle = styled.p`
  margin: 0 auto 22px auto;
  max-width: 44ch;
  color: #666d79;
  font-size: 15px;
  line-height: 1.6;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 8px;
`;

const BaseBtn = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: box-shadow 120ms ease, transform 80ms ease, background 120ms ease, color 120ms ease;

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid #2d70e2;
    outline-offset: 2px;
  }
`;

const PrimaryButton = styled(BaseBtn)`
  background: #2d70e2;
  color: #fff;
  box-shadow: 0 4px 12px rgba(45, 112, 226, 0.24);

  &:hover {
    box-shadow: 0 6px 16px rgba(45, 112, 226, 0.32);
  }
`;

const GhostButton = styled(BaseBtn)`
  background: #f4f7fe;
  color: #2a3039;
  border-color: #e3e9f6;

  &:hover {
    background: #eef3fd;
  }
`;
